import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import MessageModal from "./components/MessageModal";
import { APP_ERROR_CODE_MESSAGES, APP_ERROR_TITLE } from "./constants/appErrorMessages";
import { useEmbeddedMachineBridge } from "./hooks/useEmbeddedMachineBridge";
import { useAppRuntime } from "./hooks/useAppRuntime";
import { useAppWebSocket } from "./hooks/useAppWebSocket";
import { useMouseTouchBridge } from "./hooks/useMouseTouchBridge";
import { useRestartErrorModal } from "./hooks/useRestartErrorModal";
import Diagnostic from "./pages/Diagnostic";
import Header from "./pages/Header";
import Menu from "./pages/Menu";
import MenuLogList from "./pages/MenuLog/list";
import MenuLogLightSetting from "./pages/MenuLightSetting";
import MenuProgram from "./pages/MenuProgram";
import MenuRecipe from "./pages/MenuRecipe";
import MenuSetting from "./pages/MenuSetting";
import QuickStart from "./pages/QuickStart";
import RecordChart from "./pages/RecordChart";
import Screensaver from "./pages/Screensaver";
import { useStore } from "./store";
import { getRouterPath } from "./utils/route";

const hbVideo = require("./assets/hb-video.webm");

function isRenderableObject(value: unknown) {
  return !!value && !Array.isArray(value) && typeof value === "object";
}

function App() {
  const { dataStore } = useStore();
  const {
    isCloudMode,
    interactionMode,
    targetMachineId,
    setTargetMachineId,
    operatorName,
    setOperatorName,
    operatorId,
    setOperatorId,
    webClientId,
  } = useAppRuntime();
  const {
    messageModal,
    openMessageModal,
    closeMessageModal,
    scheduleRestartErrorMessage,
  } = useRestartErrorModal(APP_ERROR_TITLE);

  useEmbeddedMachineBridge({
    targetMachineId,
    webClientId,
    setTargetMachineId,
    setOperatorName,
    setOperatorId,
  });

  useMouseTouchBridge({
    isCloudMode,
    interactionMode,
  });

  useAppWebSocket({
    dataStore,
    isCloudMode,
    targetMachineId,
    webClientId,
    operatorName,
    operatorId,
    defaultErrorTitle: APP_ERROR_TITLE,
    errorCodeText: APP_ERROR_CODE_MESSAGES,
    openMessageModal,
  });

  const shouldRenderRoutes = useMemo(() => {
    if (isCloudMode) {
      return isRenderableObject(dataStore?.resData?.defaultAck) && isRenderableObject(dataStore?.resData?.activeLogAck);
    }
    return !!(dataStore?.resData?.defaultAck && dataStore?.resData?.activeLogAck);
  }, [dataStore?.resData?.activeLogAck, dataStore?.resData?.defaultAck, isCloudMode]);

  const pageShellStyle = useMemo(() => ({
    height: 800,
    width: 1280,
    minHeight: 800,
    minWidth: 1280,
    flexDirection: "column" as const,
    position: "relative" as const,
    flexShrink: 0,
    overflow: "hidden" as const,
    touchAction: "auto" as const,
    WebkitTapHighlightColor: interactionMode === "mouse" ? undefined : "transparent",
    cursor: "default" as const,
    userSelect: "auto" as const,
  }), [interactionMode]);

  const modalButtons = useMemo(() => ([{
    onAction: () => {
      closeMessageModal();
      scheduleRestartErrorMessage();
    },
    text: "Close",
    style: { marginTop: 50, opacity: 0.2 },
  }]), [closeMessageModal, scheduleRestartErrorMessage]);

  // App.tsx 现在只负责装配运行时 hooks、页面外壳和路由，不再承载协议细节。
  return (
    <BrowserRouter>
      <div className="remote-scroll-shell">
        <div className="flex remote-fixed-canvas" style={pageShellStyle}>
          {Object.keys(dataStore?.resData || {}).length ? <Header /> : null}

          <div
            style={{
              width: "100%",
              position: "absolute",
              top: "70px",
            }}
          >
            {!dataStore?.resData?.defaultAck?.lowSpec ? (
              <div className="absolute">
                <video autoPlay loop muted className="w-[1280px] h-[730px]">
                  <source src={hbVideo} type="video/webm" />
                </video>
              </div>
            ) : null}

            <div className="absolute">
              <Routes>
                {shouldRenderRoutes ? (
                  <>
                    <Route path={getRouterPath("/")} element={dataStore?.resData?.activeLogAck?.state === "DIAGNOSTIC" ? <Diagnostic /> : <QuickStart />} />
                    <Route path={getRouterPath("/menu")} element={<Menu />} />
                    <Route path="/record-chart" element={<RecordChart />} />
                    <Route path={getRouterPath("/menu-log")} element={<MenuLogList />} />
                    <Route path={getRouterPath("/menu-recipe")} element={<MenuRecipe />} />
                    <Route path={getRouterPath("/menu-setting")} element={<MenuSetting />} />
                    <Route path={getRouterPath("/menu-program")} element={<MenuProgram />} />
                    <Route path={getRouterPath("/menu-light-setting")} element={<MenuLogLightSetting />} />
                    <Route path="*" element={<QuickStart />} />
                  </>
                ) : (
                  <Route
                    path="*"
                    element={
                      <div style={{ width: 1280, height: 730, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 28 }}>
                        正在同步设备数据…
                      </div>
                    }
                  />
                )}
              </Routes>
            </div>
          </div>

          <MessageModal
            {...messageModal}
            buttons={modalButtons}
          />
          <Screensaver />
        </div>
      </div>
    </BrowserRouter>
  );
}

export default observer(App);

if (process.env.NODE_ENV === "production") {
  console.error = () => { };
  console.info = () => { };
  console.warn = () => { };
}
